import { useContext, useMemo, useState } from "react";

import { BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { useSpecificationsUsersToNotifyGetV2 } from "@btnet/services-btnet/handlers/specifications/specifications";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type BTSelectItem } from "types/apiResponse/apiResponse";
import { BTLoginTypes } from "types/enum";

import { isNullOrWhitespace } from "utilities/string/string";

import { BTSelectUser } from "commonComponents/btWrappers/BTSelectUser/BTSelectUser";

interface ISpecificationNotifyRecipientsEditorProps {
    jobId: number;
    includeBuilders: boolean;
    includeClients: boolean;
    includeSubs: boolean;
    onSelectionChange: (selectedUserIds: number[]) => void;
}

export const SpecificationNotifyRecipientsEditor = ({
    jobId,
    includeBuilders,
    includeClients,
    includeSubs,
    onSelectionChange,
}: ISpecificationNotifyRecipientsEditorProps) => {
    const builderInfo = useContext(BuilderInfoContext);
    // Hide the picker when the Specification Published notification pipeline is off for the builder - selecting recipients would be a no-op.
    const notificationsEnabled = !!builderInfo?.flags.enableSpecPublishNotifications;

    // Full historical selection across all audiences picked this session, including ones
    // currently hidden by an unchecked checkbox - see handleSelectionChange for why this is never
    // reset wholesale.
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

    // Fetch every audience once; checkbox toggles below only filter which groups are shown client-side.
    const { data: notifyUsersResponse, isLoading: isUsersLoading } =
        useSpecificationsUsersToNotifyGetV2(
            {
                jobId,
                loginTypes: [BTLoginTypes.BUILDER, BTLoginTypes.CONTACT, BTLoginTypes.SUBS],
            },
            { query: { enabled: notificationsEnabled } },
        );

    const allGroupsByLoginType = useMemo(() => {
        const groups = notifyUsersResponse?.data.notifyUsers ?? [];
        const map = new Map<number, BTSelectItem>();
        groups.forEach((group, index) => {
            const users = (group.options ?? []).filter(
                (user) => user.id !== undefined && user.id !== 0 && !isNullOrWhitespace(user.name),
            );
            if (users.length === 0) return;
            // Fall back to the array index (not a shared sentinel like -1) so multiple groups
            // missing a type don't collide and silently overwrite each other in the map.
            const groupType = group.type ?? index;
            map.set(groupType, {
                key: group.name ?? `group-${groupType}`,
                id: group.name ?? `group-${groupType}`,
                value: undefined,
                title: group.name ?? "Group",
                disabled: false,
                selected: false,
                children: users.map((user) => ({
                    key: user.key ?? user.id!.toString(),
                    id: user.id!.toString(),
                    value: user.id!,
                    title: user.name!,
                    disabled: !!user.disabled,
                    selected: false,
                })),
            });
        });
        return map;
    }, [notifyUsersResponse?.data.notifyUsers]);

    const notifyUserGroups = useMemo((): BTSelectItem[] => {
        const groups: BTSelectItem[] = [];
        if (includeBuilders && allGroupsByLoginType.has(BTLoginTypes.BUILDER))
            groups.push(allGroupsByLoginType.get(BTLoginTypes.BUILDER)!);
        if (includeClients && allGroupsByLoginType.has(BTLoginTypes.CONTACT))
            groups.push(allGroupsByLoginType.get(BTLoginTypes.CONTACT)!);
        if (includeSubs && allGroupsByLoginType.has(BTLoginTypes.SUBS))
            groups.push(allGroupsByLoginType.get(BTLoginTypes.SUBS)!);
        return groups;
    }, [allGroupsByLoginType, includeBuilders, includeClients, includeSubs]);

    // Force a remount whenever the set of rendered groups changes, mirroring
    // DailyLogPresentational's usersToNotifyKey - BTSelectUser doesn't reliably re-render off a
    // changed treeData prop alone.
    const selectKey = useMemo(
        () => `specificationUsersToNotify_${jobId}_${notifyUserGroups.map((g) => g.key).join("_")}`,
        [jobId, notifyUserGroups],
    );

    const validUserIds = useMemo(
        () =>
            new Set(
                notifyUserGroups.flatMap((group) => group.children!.map((c) => c.value as number)),
            ),
        [notifyUserGroups],
    );

    // Ids belonging to a currently-unchecked audience are hidden from display and never
    // submitted, but derived here (not synced into state) so unchecking and rechecking a
    // checkbox restores the prior picks without any reactive reset.
    const visibleSelectedUsers = useMemo(
        () => selectedUsers.filter((id) => validUserIds.has(id)),
        [selectedUsers, validUserIds],
    );

    // Ignores includeBuilders - publish mode always includes builders regardless of checkboxes.
    const noAudienceChecked = !includeClients && !includeSubs;

    if (!notificationsEnabled) {
        return null;
    }

    const handleSelectionChange = (_field: string, value: number[]) => {
        // BTSelectUser only offers currently-visible groups as options, so `value` (the select's
        // full current selection) only ever contains ids from those groups. Splice it back in
        // with the untouched hidden portion so toggling a checkbox off and back on doesn't lose
        // picks made under a still-unchecked audience.
        setSelectedUsers((currentUsers) => {
            const hiddenSelections = currentUsers.filter((id) => !validUserIds.has(id));
            return [...hiddenSelections, ...value];
        });
        // Hidden/unchecked-audience ids are intentionally excluded from what gets submitted.
        onSelectionChange(value);
    };

    const handleBlur = () => {
        // BTSelectUser requires onBlur - without it, closing the dropdown throws before the
        // component can update its own open state, leaving the dropdown stuck open.
    };

    return (
        <>
            <BTText size="normal-md">
                Notify Users
                <BTPopover content="Users who have notifications disabled will not show" />
            </BTText>
            <BTSelectUser
                key={selectKey}
                id="specificationUsersToNotify"
                multiple
                disabled={isUsersLoading || noAudienceChecked}
                value={visibleSelectedUsers}
                treeData={notifyUserGroups}
                onChange={handleSelectionChange}
                onBlur={handleBlur}
                showAssignToMe={false}
                data-testid="specification-notify-recipients-select"
            />
        </>
    );
};
